import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, Message, Panel, Placeholder, SelectPicker, Tag, Tooltip, Whisper } from 'rsuite';
import { Button as NsiButton, ConfirmModal, FilterField, FilterReadonly, FilterToolbar, Modal, SortableTh, sortRows, useLastPresent, useTableSort } from '@nsi-advisory-services/components';
import {
  countWorkingDays,
  getCurrentPayPeriod,
  getPayPeriodOptions,
  listWorkingDays,
  toDateOnlyString,
  formatPeriodLabel,
  STANDARD_WORK_DAY_HOURS,
} from '../utils/payPeriods';
import PageHeader from '../components/shared/PageHeader';
import HoursMeasureLabel from '../components/shared/HoursMeasureLabel';
import ViewHoursHelpModal from '../components/viewHours/ViewHoursHelpModal';
import ProjectionSettingsModal from '../components/viewHours/ProjectionSettingsModal';
import Section from '../components/shared/Section';
import TimeOffStatusBadge from '../components/timeoff/TimeOffStatusBadge';
import {
  useEmployees,
  useTimeOffRequests,
  useTimesheetValidator,
  useUpdateEmployee,
  useUpdateTimesheetReviewCheck,
  useVacationPlans,
} from '../hooks/useQueries';
import { planRemindersForRange } from '../utils/planReminders';
import { formatTripRange, hrs } from '../utils/vacationPlanFormat';
import { showToast } from '../utils/toast';
import { tawhPeriodsInRange } from '../utils/tawh';
import { useAuth } from '../contexts/AuthContext';
import './ViewHoursPage.css';

// Entry rows shown before the table is expanded. A full pay period runs to well
// over a hundred rows, which buries everything below it.
const COLLAPSED_ENTRY_COUNT = 10;

// TAWH lands on quarter hours, so a projection this close to the target is a
// match rather than a miss worth warning about.
const ON_TRACK_TOLERANCE_HOURS = 0.5;

// A close date pays a day, and both kinds of employee get that day rounded up
// to the next whole hour. What differs is the day being rounded.
//
// The epsilon keeps a value that lands exactly on an hour from being rounded up
// a step by floating-point drift.
const roundUpToHour = (hours) => {
  const value = Number(hours);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.ceil(value - 1e-9);
};

// Exempt: the standard day at their share of it. 80% of TAWH gives 6.4 and so
// credits 7; 50% gives exactly 4 and credits 4.
const getScheduledDayHours = (tawhPercent) =>
  roundUpToHour(STANDARD_WORK_DAY_HOURS * ((Number(tawhPercent) || 0) / 100));

// Non-exempt: what they actually work in a day, averaged over the last four
// complete weeks. There is no commitment percentage to scale the standard day
// by, and a flat eight would over-credit somebody on a short week and
// under-credit somebody on a long one — the closure should be worth roughly the
// day it cost them.
//
// A five-day week is the divisor rather than the days they logged: a week where
// they worked four days is a week with a day not worked in it, and dividing it
// away would report their long days as their ordinary one.
const WEEK_WORKING_DAYS = 5;

const getRecentDailyAverage = (recentWeeklyAverage) => {
  const perWeek = Number(recentWeeklyAverage?.hoursPerWeek);
  if (!Number.isFinite(perWeek) || perWeek <= 0) return null;
  return perWeek / WEEK_WORKING_DAYS;
};

// The entry table's columns; every one of them sorts.
const ENTRY_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'task', label: 'Project / Task' },
  { key: 'description', label: 'Description' },
  { key: 'hours', label: 'Hours', align: 'right' },
  { key: 'billable', label: 'Billable' },
];

const formatHours = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const rounded = Math.round((num + Number.EPSILON) * 100) / 100;
  const asInt = Math.round(rounded);
  if (Math.abs(rounded - asInt) < 0.00001) return String(asInt);
  return String(rounded);
};

const formatRefreshedAgo = (isoValue, nowTs) => {
  if (!isoValue) return null;

  const pulledAtEpoch = new Date(isoValue).getTime();
  if (!Number.isFinite(pulledAtEpoch)) return null;

  const elapsedMs = Math.max(0, nowTs - pulledAtEpoch);
  const totalMinutes = Math.floor(elapsedMs / (60 * 1000));
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);

  if (totalMinutes < 60) {
    return `${totalMinutes} min${totalMinutes === 1 ? '' : 's'}`;
  }

  if (days < 1) {
    const hours = totalHours;
    const mins = totalMinutes % 60;
    if (mins === 0) return `${hours} hour${hours === 1 ? '' : 's'}`;
    return `${hours} hour${hours === 1 ? '' : 's'} ${mins} min${mins === 1 ? '' : 's'}`;
  }

  const hoursRemainder = totalHours % 24;
  return `${days} day${days === 1 ? '' : 's'} ${hoursRemainder} hour${hoursRemainder === 1 ? '' : 's'}`;
};

/*
  One term of an equation on this card: a figure, and the label naming it on the
  row underneath, as a single two-row cell.

  The pair is one box rather than two so that a tooltip belongs to the term
  instead of to whichever half the pointer happened to find. Hovering the number
  and hovering its label used to raise two separate popups carrying the same
  sentence, which read as two facts about two things.

  The rows are the equation's own, taken through `grid-template-rows: subgrid`:
  every figure lands on the figure row and every label on the label row, which
  is what keeps the numbers of a long equation on one line rather than each term
  stacking to its own height.
*/
const EquationTerm = ({ column, tone, value, unit, label, tooltip, warning }) => {
  const cell = (
    <span
      className={`view-hours-inline-term${tooltip ? ' view-hours-inline-term--hint' : ''}`}
      style={{ gridColumn: column, gridRow: '1 / span 2' }}
    >
      <span className="view-hours-inline-figure">
        <span className={`view-hours-inline-num view-hours-inline-num--${tone}`}>{value}</span>
        {unit ? (
          <span
            className={`view-hours-inline-unit${tone === 'alert' ? ' view-hours-inline-unit--alert' : ''}`}
          >
            hrs
          </span>
        ) : null}
      </span>
      <span className="view-hours-inline-label">
        {label}
        {/* A term that needs looking at says so louder than one that merely has
            something to read. Both raise the same popup - the whole cell is the
            hover target - so the icon is only ever the signal, never the way in. */}
        {warning ? (
          <i
            className="bi bi-exclamation-triangle-fill view-hours-inline-label__hint view-hours-inline-label__hint--warn"
            aria-hidden="true"
          />
        ) : tooltip ? (
          <i className="bi bi-info-circle view-hours-inline-label__hint" aria-hidden="true" />
        ) : null}
      </span>
    </span>
  );

  if (!tooltip) return cell;

  return (
    <Whisper trigger="hover" placement="top" speaker={<Tooltip>{tooltip}</Tooltip>}>
      {cell}
    </Whisper>
  );
};

const validationSeverity = (validation) => {
  if (validation?.severity) return validation.severity;
  return validation?.passed ? 'pass' : 'fail';
};

const failureSeverity = (failure) => (failure?.severity === 'warn' ? 'warn' : 'fail');

// What the result cell says about a rule that did not pass. Most rules report a
// failure per day, some per entry; a rule measured over the whole period sends
// its own label, since "1 day" would say nothing about it.
const validationResultLabel = (validation) => {
  if (validation?.resultLabel) return validation.resultLabel;
  const count = (validation?.failures || []).length;
  const unit = validation?.unit === 'entry' ? 'entr' : 'day';
  const plural = validation?.unit === 'entry' ? (count === 1 ? 'y' : 'ies') : (count === 1 ? '' : 's');
  return `${count} ${unit}${plural}`;
};

// Prefer the project path, then the task name, then a stable fallback.
const taskLabel = (task) => {
  if (task.breadcrumb) return task.breadcrumb;
  if (task.taskName) return task.taskName;
  if (task.taskId && task.taskId !== '0') return `Task ${task.taskId}`;
  return 'No task';
};

// Hours as a headline figure. A figure the API did not send is an em dash, not
// the zero `formatHours` gives a table cell — a tile reading "0 hrs" states
// something the data never said.
const metricHours = (value) => (
  Number.isFinite(Number(value)) ? `${formatHours(value)} hrs` : '—'
);

const SummaryStat = ({ label, value, foot }) => (
  <div className="bdc-metric">
    <div className="bdc-metric__body">
      {/* Label, figure and caption are one stack: the caption reads as part of
          the number above it, the same way each figure in the time off tile
          carries its own. */}
      <div>
        <p className="bdc-metric__label">{label}</p>
        <p className="bdc-metric__value bdc-num">{value}</p>
        {foot ? <div className="bdc-metric__foot bdc-num">{foot}</div> : null}
      </div>
    </div>
  </div>
);

// When a reviewer ticked a check, e.g. "Oct 1, 2:14 PM".
const formatCheckedAt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// Short, timezone-safe date label from a YYYY-MM-DD string (e.g. "Jul 2").
const formatShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatRequestRange = (startIso, endIso) => {
  const s = formatShortDate(startIso);
  const e = formatShortDate(endIso);
  if (!s) return e;
  if (!e || s === e) return s;
  return `${s} – ${e}`;
};

// Time off overlapping the selected range: the hours approved beside the hours
// actually logged to the paid time off task in TimeCamp, plus each request with
// a status badge.
const TimeOffStat = ({ timeOff }) => {
  const requests = timeOff?.requests || [];
  const bookedHours = Number(timeOff?.totalHours) || 0;
  const loggedHours = Number(timeOff?.loggedHours) || 0;

  return (
    <div className="bdc-metric bdc-metric--wide">
      <div className="bdc-metric__body">
        <div>
          <p className="bdc-metric__label">Time Off</p>
          <div className="bdc-metric__figures">
            <div>
              <p className="bdc-metric__value bdc-num">{metricHours(bookedHours)}</p>
              <div className="bdc-metric__foot">Hours booked</div>
            </div>
            <div>
              <p className="bdc-metric__value bdc-num">{metricHours(loggedHours)}</p>
              <div className="bdc-metric__foot">Logged in TimeCamp</div>
            </div>
          </div>
        </div>
        {requests.length > 0 ? (
          <ul className="view-hours-timeoff-requests">
            {requests.map((req) => (
              <li key={req.id}>
                <span className="bdc-num">
                  {formatRequestRange(req.startDate, req.endDate)}
                  {req.inRangeHours != null ? ` · ${formatHours(req.inRangeHours)} hrs` : ''}
                  {req.totalHours != null
                    && req.inRangeHours != null
                    && req.inRangeHours !== req.totalHours
                    ? ` (of ${formatHours(req.totalHours)})` : ''}
                </span>
                <TimeOffStatusBadge status={req.status} approvalReason={req.approvalReason} size="sm" />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

/**
 * View Hours.
 *
 * Everyone gets the same page for themselves: their hours target, their
 * logged TimeCamp hours, and the timesheet behind it (summary, validations,
 * tasks, entries). Running it for someone else is what needs permission --
 * manage-all for anyone, manage-coached for your own coachees -- so for everyone
 * else no employee picker is rendered at all.
 */
// The period's entries, sortable, showing the first {COLLAPSED_ENTRY_COUNT} until
// expanded. Remounted (by key) whenever the list is a different one.
function EntriesTable({ entries }) {
  // The API hands entries back newest first, so that is where sorting starts.
  const entrySort = useTableSort('date', 'desc');
  const [isEntriesExpanded, setIsEntriesExpanded] = useState(false);
  const [entryHeights, setEntryHeights] = useState({ collapsed: null, full: null });
  const entriesTableRef = useRef(null);

  const sortedEntries = useMemo(
    () => sortRows(entries, entrySort.sortKey, entrySort.sortDir, {
      date: (entry) => String(entry?.date ?? ''),
      task: (entry) => taskLabel(entry),
      description: (entry) => String(entry?.description ?? ''),
      hours: (entry) => Number(entry?.durationHours ?? 0),
      billable: (entry) => (entry?.billable ? 'Billable' : 'Non-billable'),
    }),
    [entries, entrySort.sortKey, entrySort.sortDir]
  );

  const canExpandEntries = sortedEntries.length > COLLAPSED_ENTRY_COUNT;

  // Rows wrap, so the collapsed height has to be measured rather than assumed:
  // a fixed value would slice the tenth row in half. Both heights are kept up to
  // date as the table reflows, which is also what lets max-height animate.
  useLayoutEffect(() => {
    const table = entriesTableRef.current;
    if (!table) return undefined;

    const measure = () => {
      const rows = table.tBodies[0]?.rows || [];
      const tableTop = table.getBoundingClientRect().top;
      const full = table.getBoundingClientRect().height;
      const cutoffRow = rows.length > COLLAPSED_ENTRY_COUNT ? rows[COLLAPSED_ENTRY_COUNT] : null;
      const collapsed = cutoffRow ? cutoffRow.getBoundingClientRect().top - tableTop : full;

      setEntryHeights((prev) => (
        prev.full === full && prev.collapsed === collapsed ? prev : { collapsed, full }
      ));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(table);
    return () => observer.disconnect();
  }, [sortedEntries]);

  // Left unset until measured, and whenever there is nothing to collapse, so the
  // table is never clipped by a stale height.
  const entriesMaxHeight = !canExpandEntries
    ? undefined
    : (isEntriesExpanded ? entryHeights.full : entryHeights.collapsed) ?? undefined;

  return (
    <div className="nsi-table-wrap">
      <div className="bdc-table-head">
        <div className="bdc-table-head__heading">
          <h3 className="bdc-table-head__title">Entries</h3>
          <span className="bdc-count">{entries.length}</span>
        </div>
      </div>
      <div
        className={`bdc-table-collapse${canExpandEntries && !isEntriesExpanded ? ' is-clipped' : ''}`}
        style={{ maxHeight: entriesMaxHeight }}
      >
        <table ref={entriesTableRef} className="nsi-table">
          <thead>
            <tr>
              {ENTRY_COLUMNS.map((column) => (
                <SortableTh key={column.key} sort={entrySort} sortKey={column.key} align={column.align}>
                  {column.label}
                </SortableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedEntries.length === 0 ? (
              <tr>
                <td colSpan={ENTRY_COLUMNS.length} className="nsi-table__empty">
                  No timesheet entries for this period.
                </td>
              </tr>
            ) : sortedEntries.map((entry, idx) => (
              <tr key={entry.id || `${entry.date}-${idx}`}>
                <td className="bdc-num">{entry.date || '—'}</td>
                <td>{taskLabel(entry)}</td>
                <td className="bdc-table-wrap nsi-table__muted">
                  {entry.description || '—'}
                </td>
                <td className="nsi-table__right bdc-num">{formatHours(entry.durationHours)}</td>
                <td>
                  {entry.billable ? (
                    <Tag color="green">Billable</Tag>
                  ) : (
                    <Tag>Non-billable</Tag>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canExpandEntries ? (
        <button
          type="button"
          className="bdc-table-action"
          onClick={() => setIsEntriesExpanded((prev) => !prev)}
          aria-expanded={isEntriesExpanded}
        >
          {isEntriesExpanded
            ? `Show first ${COLLAPSED_ENTRY_COUNT}`
            : `Show all ${sortedEntries.length} entries`}
          <i className="bi bi-chevron-down bdc-table-chevron" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

export default function ViewHoursPage() {
  const auth = useAuth();

  // Validating your own timesheet needs no permission at all. Someone else's is
  // what the endpoint gates, on the same manage permissions that decide whose
  // record you can see anywhere else, so that is what decides whether the person
  // on screen can be changed.
  const canSelectOthers = auth.hasPermission('manage_all_employees')
    || auth.hasPermission('manage_coached_employees');

  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshNowTs, setRefreshNowTs] = useState(() => Date.now());
  const [openValidation, setOpenValidation] = useState(null);
  const validationShown = useLastPresent(openValidation); // keeps the dialog steady while it fades out
  const [helpOpen, setHelpOpen] = useState(false);
  const [projectionSettingsOpen, setProjectionSettingsOpen] = useState(false);

  const payPeriodOptions = useMemo(() => [...getPayPeriodOptions()].reverse(), []);
  const currentPeriod = useMemo(() => getCurrentPayPeriod(), []);
  const currentPeriodValue = `${toDateOnlyString(currentPeriod.start)}|${toDateOnlyString(currentPeriod.end)}`;

  // Filters live in the URL so they persist across reloads and are shareable.
  const urlEmployeeId = searchParams.get('employeeId') || '';
  const urlStart = searchParams.get('start') || '';
  const urlEnd = searchParams.get('end') || '';

  const ownEmployeeId = auth.employee?.id ?? null;
  const selectedEmployeeId = canSelectOthers ? (urlEmployeeId || ownEmployeeId) : ownEmployeeId;
  const isSelf = String(selectedEmployeeId || '') === String(ownEmployeeId || '');

  // Resolve the URL's start/end dates back to pay-period option values. Falls
  // back to the current pay period when the URL has no (or an unknown) start.
  const startPeriod = useMemo(() => {
    const match = payPeriodOptions.find((opt) => String(opt.value).split('|')[0] === urlStart);
    return match ? match.value : currentPeriodValue;
  }, [payPeriodOptions, urlStart, currentPeriodValue]);
  const endPeriod = useMemo(() => {
    if (!urlEnd) return null;
    const match = payPeriodOptions.find((opt) => String(opt.value).split('|')[1] === urlEnd);
    // An end matching the start period itself is just a single-period selection.
    if (!match || match.value === startPeriod) return null;
    return match.value;
  }, [payPeriodOptions, urlEnd, startPeriod]);

  // Merge filter updates into the URL query (replace to avoid history spam).
  const setFilters = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  const employeesQuery = useEmployees({ enabled: canSelectOthers });
  const employeeOptions = useMemo(
    () =>
      (employeesQuery.data || [])
        .filter((emp) => emp?.is_active !== false)
        .map((emp) => ({
          label: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email || 'Unknown',
          value: emp.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [employeesQuery.data]
  );

  // End-period choices are limited to periods on/after the selected start period.
  const endPeriodOptions = useMemo(() => {
    const [startIso] = String(startPeriod || '').split('|');
    return payPeriodOptions.filter((opt) => {
      const [optStart] = String(opt.value).split('|');
      return optStart >= startIso;
    });
  }, [payPeriodOptions, startPeriod]);

  const { start, end } = useMemo(() => {
    const [s, endOfStart] = String(startPeriod || '').split('|');
    if (!endPeriod) return { start: s || '', end: endOfStart || '' };
    const [, e] = String(endPeriod).split('|');
    return { start: s || '', end: e || endOfStart || '' };
  }, [startPeriod, endPeriod]);

  const isRangeMode = Boolean(endPeriod);

  const validatorQuery = useTimesheetValidator(
    { employeeId: selectedEmployeeId, start, end },
    { enabled: Boolean(selectedEmployeeId && start && end) }
  );

  const data = validatorQuery.data;
  const summary = data?.summary || null;

  /*
    What a submitted vacation plan counts on in the pay periods on screen:
    extra hours to work before the trip, time off taken unpaid. Plans are
    private, so this is only ever on somebody's own timesheet.
  */
  const plansQuery = useVacationPlans({ enabled: isSelf && Boolean(ownEmployeeId) });
  const ownRequestsQuery = useTimeOffRequests({ enabled: isSelf && Boolean(ownEmployeeId) });
  const planReminders = useMemo(() => {
    if (!isSelf) return [];
    return planRemindersForRange({
      plans: Array.isArray(plansQuery.data) ? plansQuery.data : [],
      requests: (ownRequestsQuery.data || []).filter((row) => String(row.employee_id) === String(ownEmployeeId)),
      start,
      end,
    });
  }, [end, isSelf, ownEmployeeId, ownRequestsQuery.data, plansQuery.data, start]);

  const allEntries = useMemo(() => data?.entries || [], [data?.entries]);


  // The percentage is a fact about the employee, not a setting of this page: it
  // is what their hours are actually reduced by, and the whole page is an answer
  // to "against what am I measured". It is also dated - people move up and down
  // - so it comes from the validator, which answers for the range on screen
  // (as of its end) rather than for today. Reading the employee record here is
  // what used to re-mark old pay periods after a change.
  const selectedPercentNumber = useMemo(() => {
    const parsed = Number(data?.employee?.tawhPercent);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [data?.employee?.tawhPercent]);
  // Non-exempt employees are not held to a TAWH commitment: no percentage
  // applies to them, so there is no target to project against and nothing for
  // the reduced view to show. What they actually worked is the whole story.
  const isPartTime = Boolean(data?.employee?.partTime);

  // Each whole pay period's own commitment, keyed by its first day.
  const commitmentByPeriodStart = useMemo(
    () => new Map((data?.periodCommitments || []).map((commitment) => [commitment.start, commitment])),
    [data?.periodCommitments]
  );

  const recentWeeklyAverage = data?.recentWeeklyAverage;

  // Hours to work, one row per whole pay period the range covers, each measured
  // against that period's own commitment. The per-period split is not
  // presentational: TAWH rounds, so a three-period range is the sum of three
  // rounded results and not the formula applied to the whole span - and a range
  // can span a change of commitment, even of classification.
  //
  //   exempt period       TAWH at the period's percentage
  //   non-exempt period   the period's target hours; null when none was set
  const rangeRows = useMemo(
    () => tawhPeriodsInRange(start, end, (period) => {
      const percent = Number(commitmentByPeriodStart.get(period.startDate)?.tawhPercent);
      return Number.isFinite(percent) ? percent : selectedPercentNumber;
    }).map((row) => {
      const own = commitmentByPeriodStart.get(row.startDate);
      const nonExempt = own ? own.nonExempt === true : isPartTime;
      const target = own ? own.targetHours : data?.employee?.targetHours;
      const targetNumber = target == null || target === '' ? null : Number(target);
      return {
        ...row,
        nonExempt,
        targetHours: Number.isFinite(targetNumber) ? targetNumber : null,
        hoursToWork: nonExempt ? (Number.isFinite(targetNumber) ? targetNumber : null) : row.tawhHours,
      };
    }),
    [start, end, commitmentByPeriodStart, selectedPercentNumber, isPartTime, data?.employee?.targetHours]
  );

  // The percentages the exempt periods were measured at, for the "% of TAWH"
  // figure: one in nearly every case, more when the range spans a change. A
  // non-exempt period's stored percentage is not read, so it is left out.
  const rangePercents = useMemo(
    () => [...new Set(rangeRows.filter((row) => !row.nonExempt).map((row) => row.tawhPercent))],
    [rangeRows]
  );

  // What the range is measured against: every period's hours to work added up.
  // Null when there is nothing to measure against - no whole pay period on
  // screen for an exempt employee, or no target set for any period of a
  // non-exempt one - in which case the card shows only the hours worked.
  //
  // Less than a whole period on screen for a non-exempt employee falls back to
  // that period's target, as it always has.
  const periodTarget = useMemo(() => {
    if (rangeRows.length === 0) {
      if (!isPartTime) return null;
      const target = Number(data?.employee?.targetHours);
      return data?.employee?.targetHours == null || !Number.isFinite(target) ? null : target;
    }
    const measured = rangeRows.filter((row) => row.hoursToWork != null);
    if (measured.length === 0) return null;
    return measured.reduce((sum, row) => sum + row.hoursToWork, 0);
  }, [rangeRows, isPartTime, data?.employee?.targetHours]);

  const workedHours = summary ? Number(summary.totalHours) : undefined;
  const remainingHours =
    periodTarget != null && workedHours !== undefined
      ? periodTarget - workedHours
      : undefined;

  // Projection side.
  const todayIso = useMemo(() => toDateOnlyString(new Date()), []);
  const isCurrentPeriod = Boolean(start && end && todayIso >= start && todayIso <= end);
  const isTodayWorkingDay = useMemo(() => countWorkingDays(todayIso, todayIso) === 1, [todayIso]);

  // What today already has against it. Today is all-or-nothing in the
  // projection below, so this is read as a yes/no rather than for its size.
  const hoursLoggedToday = useMemo(() => {
    if (!isCurrentPeriod) return 0;

    return allEntries.reduce(
      (sum, entry) => (entry.date === todayIso ? sum + Number(entry.durationHours || 0) : sum),
      0
    );
  }, [isCurrentPeriod, allEntries, todayIso]);

  // Approved time off per day, already spread over the request's weekdays by the
  // server so a multi-day request divides up the same way everywhere.
  /*
    Approved time off per day, already spread over the request's weekdays by the
    server so a multi-day request divides up the same way everywhere.

    Three maps rather than one, because only the first of them will ever be
    logged against the paid time off task - see the rule in remainingDays. The
    fallback to the combined figure is for a client running ahead of a server
    that does not send the split yet: every hour reads as paid, which is what
    this page did before the three were told apart.
  */
  const timeOffHoursByDate = useMemo(
    () => data?.timeOff?.approvedHoursByDate || data?.timeOff?.hoursByDate || {},
    [data?.timeOff?.approvedHoursByDate, data?.timeOff?.hoursByDate]
  );

  // Time off entered without a review, which the request form asks for only
  // when no reimbursement is expected. Recorded, unpaid, never logged.
  const timeOffEnteredHoursByDate = useMemo(
    () => data?.timeOff?.enteredHoursByDate || {},
    [data?.timeOff?.enteredHoursByDate]
  );

  // Filed for review and still waiting on a decision. Empty for a viewer who is
  // not allowed to see somebody else's pending requests - the server's call.
  const timeOffPendingHoursByDate = useMemo(
    () => data?.timeOff?.pendingHoursByDate || {},
    [data?.timeOff?.pendingHoursByDate]
  );

  // What is actually on the paid time off task in TimeCamp, by date. The
  // projection counts approved time off from the day it is approved rather than
  // the day somebody gets round to logging it - nobody should have to log time
  // off a fortnight early to make this card tell the truth - so this is only
  // ever read to say which of those hours TimeCamp has not caught up with yet.
  const timeOffLoggedHoursByDate = useMemo(
    () => data?.timeOff?.loggedHoursByDate || {},
    [data?.timeOff?.loggedHoursByDate]
  );

  // How long this person's ordinary day is. Read from the same two places every
  // other per-employee figure on this page comes from: the auth record when it
  // is your own timesheet, the validator payload when it is somebody else's.
  //
  // Null is a real value here - it means nobody has been asked yet - so it is
  // carried rather than defaulted at the point of reading.
  const averageWorkdayHours = useMemo(() => {
    const source = isSelf
      ? auth.employee?.average_workday_hours
      : data?.employee?.averageWorkdayHours;
    if (source == null || source === '') return null;
    const parsed = Number(source);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [isSelf, auth.employee?.average_workday_hours, data?.employee?.averageWorkdayHours]);

  // Hiding the projection is a display preference of whoever is reading, so
  // only the reader's own setting applies. A coach reviewing somebody who has
  // hidden theirs still needs to see it - that is what the review is for.
  const hideProjectionSetting = isSelf && Boolean(auth.employee?.hide_hours_projection);

  // What a remaining working day is worth in the projection. The fallback is
  // never shown: with no answer the page asks for one instead (see
  // needsWorkdayAnswer), whoever's timesheet it is. Assuming eight for somebody
  // else read as the reviewer's own day and threw their projection off.
  const projectionDayHours = averageWorkdayHours ?? STANDARD_WORK_DAY_HOURS;

  // What one close date is worth. Two different rules, because the two kinds of
  // employee have two different ideas of a day: exempt staff have a committed
  // share of the standard day, non-exempt have only what they actually work.
  //
  // The fallback for a non-exempt with no recent average - a new hire, or a
  // TimeCamp call that came back empty - is the working day they told us about,
  // rounded the same way. It is the same question answered by hand.
  const recentDailyAverage = getRecentDailyAverage(recentWeeklyAverage);

  const scheduledDayHours = isPartTime
    ? roundUpToHour(recentDailyAverage ?? averageWorkdayHours ?? STANDARD_WORK_DAY_HOURS)
    : getScheduledDayHours(selectedPercentNumber);

  // What to offer as the answer to "how long is your working day". Nobody has
  // to work it out from their own timesheet when the timesheet already knows:
  // it is the same recent average the close-date rule runs on, at whatever
  // quarter hour is nearest so the number reads as a working day rather than a
  // measurement. Null when there is no recent history to suggest from.
  const suggestedWorkdayHours = !isPartTime || recentDailyAverage == null
    ? null
    : Math.round(recentDailyAverage * 4) / 4;

  // Days the office is shut. Weekend closures fall out on their own, since only
  // working days are walked below.
  const closeDateSet = useMemo(() => {
    const dates = data?.closeDates || [];
    return new Set(dates);
  }, [data?.closeDates]);

  // Hours actually booked against the close date task, by date. Comes from the
  // server rather than being sifted out of the entry list here, because knowing
  // which TimeCamp task is the close date one is a server fact - see the
  // closeDate block in routes/timeCamp.js.
  const closeDateLoggedByDate = useMemo(
    () => data?.closeDate?.hoursByDate || {},
    [data?.closeDate?.hoursByDate]
  );

  /*
    Closures already behind us that nobody has logged yet.

    A closure earlier in the period falls out of the walk below, which starts at
    today - so it credits nothing ahead. If no time was booked against it either
    it counts nothing behind, and the projection is short by a whole closed day
    for as long as it takes somebody to log it. That gap is exactly the window
    where the number matters, because it is what tells them the day is missing.

    So the credit stays on the equation until the hours are actually there. The
    moment anything lands on the close date task for that day it drops out of
    here and is counted in hours to date instead, where it belongs - never both.
  */
  const unloggedPastCloseDates = useMemo(() => {
    if (!isCurrentPeriod) return [];
    return listWorkingDays(start, todayIso)
      .filter((date) => date < todayIso)
      .filter((date) => closeDateSet.has(date))
      .filter((date) => !(Number(closeDateLoggedByDate[date]) > 0));
  }, [isCurrentPeriod, start, todayIso, closeDateSet, closeDateLoggedByDate]);

  // One entry per working day still ahead, and what each deduction takes off it.
  // The parts are tracked separately so the equation can show them as their own
  // subtracted terms, and because attributing a day to the wrong one would make
  // the arithmetic on screen stop adding up.
  const remainingDays = useMemo(() => {
    if (!isCurrentPeriod) return [];

    const ahead = listWorkingDays(todayIso, end).map((date) => {
      // Today is all or nothing.
      //
      // Once anything is logged against today, the day is taken as finished and
      // adds nothing further: what was worked is already in hours to date, and
      // topping it up to a full day forecasts hours the person has just told us
      // they are not going to work. Seven hours in and "+1 hrs left today" is
      // noise in the one number the card exists to give.
      //
      // An untouched weekday still counts a whole standard day, which is what
      // makes the projection worth reading first thing in the morning. Time off
      // and close dates are handled below and reach here as an untouched day.
      const gross = date === todayIso
        ? (hoursLoggedToday > 0 ? 0 : projectionDayHours)
        : projectionDayHours;

      // A close date settles the day on its own. There is nothing left to work,
      // so it contributes no standard hours — but it still pays the closed day,
      // which is hours the period will end up counting. Time off booked across a
      // closure is not charged against it, so it is ignored here entirely.
      //
      // The credit is the whole rounded-up day and is NOT trimmed to the length
      // of this person's working day. It used to be, which made the figure
      // disagree with the tooltip explaining it: somebody averaging 5.57 hours
      // was told the closure paid 6 and shown 5.5. Rounding up to the hour is
      // the rule, so the rounded figure is what gets paid.
      //
      // `gross` is still consulted, but only for whether the day counts at all:
      // today is all-or-nothing, so a closure today that already has hours
      // logged against it adds nothing on top of them.
      if (closeDateSet.has(date)) {
        const credit = gross > 0 ? scheduledDayHours : 0;
        return {
          date,
          gross: 0,
          closeCredit: credit,
          timeOffCredit: 0,
          enteredApplied: 0,
          pendingApplied: 0,
          approvedApplied: 0,
          timeOffUnlogged: 0,
          hours: credit,
        };
      }

      /*
        Time off takes away the share of the day it covers, and gives back only
        the part of it somebody will be paid for.

        Requests are booked against the standard eight hour day wherever they
        come from, so four hours off is half a day - and for someone whose day
        is ten that has to remove five, not four. Subtracting the booked hours
        straight off a longer day would leave a full day off still showing two
        hours to work. Scaling is also what keeps a whole day off landing on
        exactly zero at any day length, and the cap at one whole day stops an
        over-booked day pushing the result negative.

        What comes back depends on whether the hours will be paid, which is the
        whole reason the three kinds are counted apart:

          approved  reviewed and signed off, which the request form asks for
                    only when the time is expecting a reimbursement. Paid hours
                    are logged against the paid time off task, and hours to date
                    counts that task along with the rest - so they meet the
                    target exactly as worked hours do. Taken off the day and
                    credited straight back, which for an eight hour day cancels:
                    approved leave leaves the projection where it was, because
                    the work was not lost so much as paid for another way.

          entered   filed with no review because no reimbursement is expected.
                    Nothing is ever logged for it, so the hours are simply gone
                    from the period. Deducted and not credited.

          pending   filed for review and still waiting. Nothing is logged for it
                    either, so it is deducted the same way - and comes back the
                    moment somebody approves it, which is worth knowing while
                    there is still time to chase it.

        The credit is the hours booked rather than the capacity they removed,
        because the booked figure is what lands in TimeCamp: a whole day off
        books eight whatever the person's own day is worth. Holding it to the
        capacity removed keeps a day from giving back more than it took, and is
        what leaves a ten hour day two hours short over a full day off.

        The three are laid over the day in order rather than summed, so an
        over-booked day gives its hours to the paid request first and the cover
        still totals the one day the cap allows.
      */
      const approvedHours = Number(timeOffHoursByDate[date]) || 0;
      const enteredHours = Number(timeOffEnteredHoursByDate[date]) || 0;
      const pendingHours = Number(timeOffPendingHoursByDate[date]) || 0;

      const cover = (hours) => Math.min(1, hours / STANDARD_WORK_DAY_HOURS);
      const approvedCovered = cover(approvedHours);
      const enteredCovered = cover(approvedHours + enteredHours) - approvedCovered;
      const pendingCovered = cover(approvedHours + enteredHours + pendingHours)
        - cover(approvedHours + enteredHours);

      const dayCovered = approvedCovered + enteredCovered + pendingCovered;
      const afterTimeOff = Math.max(0, gross * (1 - dayCovered));
      const timeOffCredit = Math.min(approvedHours, gross * approvedCovered);

      return {
        date,
        gross,
        closeCredit: 0,
        // One field per column, because every kind of time off now has one.
        // The three deductions are hours the day loses; the credit is what
        // comes back, and only approved time off has any.
        enteredApplied: gross * enteredCovered,
        pendingApplied: gross * pendingCovered,
        approvedApplied: gross * approvedCovered,
        timeOffCredit,
        /*
          The part of today's PATO that TimeCamp has nothing against yet.

          Read for one thing only: whether the PATO column carries its flag. It
          is deliberately not added to the hours below.

          Adding it would hold the answer still while somebody logs their time
          off, by supplying hours TimeCamp has not seen and then handing them
          back as the entries arrive. That worked, but it paid for it out of the
          hours-remaining figure, which then disagreed with the same figure on
          the actuals side beside it - and a card whose two halves print
          different numbers for one thing is worse than one whose bottom line
          moves. Remaining is target less hours worked, and nothing else.

          So the projection reads a little further under target until the time
          off is logged, and the flag is what says so.
        */
        timeOffUnlogged: Math.max(
          0,
          timeOffCredit - (Number(timeOffLoggedHoursByDate[date]) || 0)
        ),
        hours: afterTimeOff,
      };
    });

    // The closures already behind us with nothing logged against them. They
    // contribute no standard hours - there was nothing to work - but they still
    // pay the closed day, so they ride on the same list the equation sums.
    const unlogged = unloggedPastCloseDates.map((date) => ({
      date,
      gross: 0,
      closeCredit: scheduledDayHours,
      timeOffCredit: 0,
      enteredApplied: 0,
      pendingApplied: 0,
      approvedApplied: 0,
      timeOffUnlogged: 0,
      hours: scheduledDayHours,
    }));

    return [...unlogged, ...ahead].sort((a, b) => a.date.localeCompare(b.date));
  }, [isCurrentPeriod, todayIso, end, timeOffHoursByDate, timeOffEnteredHoursByDate, timeOffPendingHoursByDate, timeOffLoggedHoursByDate, hoursLoggedToday, closeDateSet, scheduledDayHours, projectionDayHours, unloggedPastCloseDates]);

  const sumDays = (key) => remainingDays.reduce((sum, day) => sum + day[key], 0);
  const grossStandardHours = sumDays('gross');
  const remainingStandardHours = sumDays('hours');

  const closeDateCreditHours = sumDays('closeCredit');
  const closeDateCount = remainingDays.filter((day) => day.closeCredit > 0).length;

  // One total per column, and every one of them a deduction: time off entered
  // without a review, time off nobody has decided yet, and the working time
  // approved PATO takes up.
  const timeOffEnteredHours = sumDays('enteredApplied');
  const timeOffPendingHours = sumDays('pendingApplied');
  const patoTakenHours = sumDays('approvedApplied');

  // Approved hours the projection is counting that TimeCamp has not seen. Read
  // as a yes/no rather than for its size: it is what decides whether the paid
  // time off figure carries a flag saying it is standing on a request.
  const timeOffUnloggedHours = sumDays('timeOffUnlogged');

  // Days the office is open, which is what the standard-hours term is built
  // from. Both the term's own tooltip and the summary line under the equation
  // are spelled out from these, so they cannot describe it differently.
  // Found by date rather than by position. The list no longer opens on today:
  // a closure earlier in the period that has not been logged yet sorts ahead of
  // it, and indexing the first entry would then read that closure's zero as
  // today's remaining hours.
  const todayEntry = remainingDays.find((day) => day.date === todayIso);
  const grossHoursToday = todayEntry?.gross || 0;

  // What today still asks for once its time off is taken out, of whatever
  // kind. Only the summary line reads it: the standard term stays gross, with
  // the time off subtracted in its own columns.
  const workHoursLeftToday = todayEntry?.hours || 0;
  const isTodayPlannedOff = grossHoursToday > 0 && workHoursLeftToday < 0.01;

  // Only days still ahead. A past closure carries gross 0, so it never counted
  // here, but saying "after today" plainly keeps that true if the credit rule
  // ever changes.
  const openDaysAfterToday = remainingDays
    .filter((day) => day.date > todayIso && day.gross > 0).length;

  // Where the standard-hours figure comes from. Its two parts are found
  // differently - what is left of today, then whole days after it - and neither
  // is obvious from the total, so the arithmetic is one hover away.
  const standardTooltip = useMemo(() => {
    const parts = [];
    if (isTodayWorkingDay && grossHoursToday > 0) {
      parts.push(`${formatHours(grossHoursToday)} hrs left today`);
    }
    if (openDaysAfterToday > 0) {
      parts.push(
        `${openDaysAfterToday} working day${openDaysAfterToday === 1 ? '' : 's'}`
        + ` × ${formatHours(projectionDayHours)} hrs`
      );
    }

    const sentences = [
      parts.length > 0
        ? `${parts.join(' + ')} = ${formatHours(grossStandardHours)} hrs.`
        : 'No open working days left in this period.',
    ];

    // Where the rate in that sum comes from. It is the one number on this card
    // somebody sets by hand, so the sum is worth very little without it.
    sentences.push(
      `Every working day is counted at ${formatHours(projectionDayHours)} hrs, ${isSelf ? 'your' : 'their'}`
      + ' standard working hours in a day.'
    );

    // Says why today is missing from the sum. Without it the day looks skipped.
    if (isTodayWorkingDay && grossHoursToday === 0 && hoursLoggedToday > 0) {
      sentences.push(
        `Today is left out because ${formatHours(hoursLoggedToday)} hrs are already logged against it.`
      );
    }

    // Says what does not count as a working day. Deliberately makes no claim
    // about where close dates go instead: they have their own labelled figure
    // in the same equation, which says it better than a sentence can.
    sentences.push('Weekends and company close dates are not counted as working days.');

    return sentences.join(' ');
  }, [
    isTodayWorkingDay,
    grossHoursToday,
    openDaysAfterToday,
    projectionDayHours,
    grossStandardHours,
    hoursLoggedToday,
    isSelf,
  ]);

  // The projected equation as a list of terms, so a deduction that does not
  // apply simply takes no column. It reads as what the rest of the period will
  // produce, less what is still owed against the target, which is why the
  // result is an over/under rather than a total.
  //
  // The hours remaining are the last term and are subtracted, because that is
  // what the arithmetic does with them. They used to lead the equation with no
  // operator, which printed 75 + 88 - 21 = -8: a true answer under a sum that
  // does not add up, on the one line of the card somebody would check by hand.
  const projectionTerms = useMemo(() => {
    const terms = [
      // Where the sum starts, so no leading operator.
      //
      // Named for what it is - the standard hours still ahead of you - because
      // "my work hours in pay period" read like the TAWH target sitting on the
      // other side of the card, and the two are different numbers: this one
      // shrinks as the period is used up, the target does not move.
      {
        key: 'standard',
        op: null,
        label: `${isSelf ? 'My standard' : 'Standard'} work hours\nremaining in pay period`,
        tone: 'standard',
        value: grossStandardHours,
        tooltip: standardTooltip,
      },
    ];

    if (closeDateCreditHours >= 0.01) {
      terms.push({
        key: 'closed',
        op: '+',
        label: `Company close date${closeDateCount === 1 ? '' : 's'}`,
        tone: 'standard',
        value: closeDateCreditHours,
        // The rate is not obvious from the number alone, so the arithmetic that
        // produced it is one hover away.
        tooltip: (isPartTime
          ? (recentDailyAverage == null
            ? `${formatHours(projectionDayHours)} hrs a day`
            : `${formatHours(recentWeeklyAverage.hoursPerWeek)} hrs/week over the last`
              + ` ${recentWeeklyAverage.weeks} weeks ÷ ${WEEK_WORKING_DAYS} days`
              + ` = ${formatHours(recentDailyAverage)} hrs`)
          : `${formatHours(STANDARD_WORK_DAY_HOURS)} hrs × ${selectedPercentNumber}%`
            + ` = ${formatHours(STANDARD_WORK_DAY_HOURS * (selectedPercentNumber / 100))} hrs`)
          + `, rounded up to ${formatHours(scheduledDayHours)} hrs per close date`
          + (closeDateCount > 1 ? ` × ${closeDateCount} close dates` : ''),
      });
    }
    // Time off entered without a review. Said in the first person, the way the
    // rest of this card's labels are: it is the employee's own schedule being
    // described back to them.
    if (timeOffEnteredHours >= 0.01) {
      terms.push({
        key: 'planned',
        op: '-',
        label: 'Planned\ntime out',
        tone: 'deduction',
        value: timeOffEnteredHours,
        tooltip: (isSelf
          ? 'These are hours that I will be unavailable during my normal schedule.'
          : 'These are hours they will be unavailable during their normal schedule.')
          + ' These are not PATO hours.',
      });
    }

    // Filed for review and still waiting. Deducted like any other absence, and
    // unlike PATO nothing pays it back - until somebody approves it, at which
    // point it moves across to the two columns below.
    if (timeOffPendingHours >= 0.01) {
      terms.push({
        key: 'pending',
        op: '-',
        label: 'Unapproved\ntime off',
        tone: 'deduction',
        value: timeOffPendingHours,
        tooltip: `${formatHours(timeOffPendingHours)} hrs is still waiting on a decision.`
          + ' Nothing is logged against the paid time off task until it is approved, so'
          + ' these hours come back the moment somebody signs it off.',
      });
    }

    /*
      Approved PATO, as one deduction beside the others.

      Counted from the day it is approved rather than the day somebody logs it:
      nobody should have to go and log time off weeks early to make this card
      tell them the truth. A figure standing on a request rather than on a
      TimeCamp entry should say so though, so it carries a flag until the hours
      are actually there.

      The figure is the working time the day gives up, not the hours the request
      books. The two are the same for an eight hour day. They part company on a
      longer one, where a whole day off books eight and costs ten - and it is
      the ten that the period actually loses.
    */
    if (patoTakenHours >= 0.01) {
      const isUnlogged = timeOffUnloggedHours >= 0.01;

      terms.push({
        key: 'pato',
        op: '-',
        label: 'PATO\nhours',
        tone: 'deduction',
        value: patoTakenHours,
        warning: isUnlogged,
        // Only the flag has anything to say. Once TimeCamp has the entry there
        // is nothing left to explain, so the term carries no marker at all.
        tooltip: isUnlogged
          ? 'This has not yet been logged in TimeCamp. It is still being counted in the'
            + ' projection to help you plan.'
          : undefined,
      });
    }

    // Last, and subtracted: everything before it is work the period still has
    // in it, and this is what it owes. Target less hours worked, carried over
    // from the actuals side exactly as it reads there - it is the same figure
    // about the same period, and nothing here adjusts it.
    terms.push({
      key: 'remaining',
      op: '-',
      label: 'Left to work',
      tone: 'remaining',
      value: remainingHours,
      unit: true,
    });

    return terms;
  }, [
    remainingHours,
    grossStandardHours,
    standardTooltip,
    closeDateCreditHours,
    closeDateCount,
    scheduledDayHours,
    selectedPercentNumber,
    timeOffEnteredHours,
    timeOffPendingHours,
    patoTakenHours,
    timeOffUnloggedHours,
    isPartTime,
    recentDailyAverage,
    recentWeeklyAverage,
    projectionDayHours,
    isSelf,
  ]);

  // Column positions are walked rather than derived from the index, because a
  // term without an operator shifts everything after it. One column per term,
  // whatever it holds: the figure and its unit ride in the same cell now.
  const projectionLayout = useMemo(() => {
    let column = 1;
    const placed = projectionTerms.map((term) => {
      const opColumn = term.op ? column++ : null;
      return { ...term, opColumn, termColumn: column++ };
    });

    return {
      terms: placed,
      equalsColumn: column++,
      resultColumn: column++,
      columns: column - 1,
    };
  }, [projectionTerms]);

  const projectionAriaLabel = `${projectionTerms
    .map((term) => (term.op
      ? `${term.op === '-' ? 'minus' : 'plus'} ${term.label}`
      : term.label))
    .join(' ')} equals projected hours over or under target`;

  // Spells out where the standard-hours term comes from, since its two parts
  // are found differently: the rest of today, then whole days after it.
  const projectionBasis = useMemo(() => {
    const parts = [];
    // Today reads either way round: the hours still ahead of it while it is
    // open, the hours already on it once it is not. Time off booked against
    // today comes out first, so a day off does not read as a day to work.
    if (isTodayWorkingDay && isTodayPlannedOff) {
      parts.push('Today planned off');
    } else if (isTodayWorkingDay && grossHoursToday > 0) {
      parts.push(`${formatHours(workHoursLeftToday)} hrs left today`);
    } else if (isTodayWorkingDay && hoursLoggedToday > 0) {
      parts.push(`${formatHours(hoursLoggedToday)} hrs logged today`);
    }
    if (openDaysAfterToday > 0) {
      const days = `${openDaysAfterToday} working day${openDaysAfterToday === 1 ? '' : 's'} × ${formatHours(projectionDayHours)} hrs`;
      // Bracketed when it follows today's term, so the multiplication plainly
      // binds tighter than the addition.
      parts.push(parts.length ? `(${days})` : days);
    }

    // Note that with today already logged this line totals more than the
    // work hours term above it: today's hours are real hours the period has
    // banked, but they sit in hours worked rather than in the hours still to
    // come. The line reads as everything from the start of today onward.
    return parts.join(' + ');
  }, [
    isTodayWorkingDay,
    isTodayPlannedOff,
    grossHoursToday,
    workHoursLeftToday,
    hoursLoggedToday,
    openDaysAfterToday,
    projectionDayHours,
  ]);

  // Where the period lands if every working day left is worked at the standard
  // schedule, and how far that is from the reduced target.
  const projectedHours =
    workedHours !== undefined ? workedHours + remainingStandardHours : undefined;
  const projectedVariance =
    projectedHours !== undefined && periodTarget != null
      ? projectedHours - periodTarget
      : undefined;
  const isOnTrack =
    projectedVariance !== undefined && Math.abs(projectedVariance) < ON_TRACK_TOLERANCE_HOURS;

  // Only the period being lived through has anything to project: a closed one
  // is already its own answer, and a future one has no hours worked to build on.
  //
  // Both classifications get one. What each is measured against differs - a
  // commitment for exempt staff, a set number of hours per period for
  // non-exempt - but that is already resolved into `periodTarget`, and a
  // non-exempt employee with no target set falls out here on its own.
  const hasProjection = !isRangeMode
    && isCurrentPeriod
    && Number.isFinite(projectedVariance);

  // Refreshing is a forced refetch of the timesheet: that endpoint reads TimeCamp
  // live, and it is what feeds every number on this page.
  const canPullHours = Boolean(selectedEmployeeId && start && end);
  const isPullingHours = validatorQuery.isFetching;
  const lastRefreshedAt = validatorQuery.dataUpdatedAt
    ? new Date(validatorQuery.dataUpdatedAt).toISOString()
    : null;
  const refreshedAgo = formatRefreshedAgo(lastRefreshedAt, refreshNowTs);

  const periodLabel = useMemo(() => {
    if (!start || !end) return '';
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
    return formatPeriodLabel(s, e);
  }, [start, end]);

  useEffect(() => {
    if (!lastRefreshedAt) return undefined;
    const id = window.setInterval(() => {
      setRefreshNowTs(Date.now());
    }, 60 * 1000);

    return () => window.clearInterval(id);
  }, [lastRefreshedAt]);

  const handleStartPeriodChange = (value) => {
    if (!value) return;
    const [newStart] = String(value).split('|');
    let nextEnd = urlEnd;
    // Drop an end period that now precedes the new start period.
    if (endPeriod) {
      const [endStart] = String(endPeriod).split('|');
      if (endStart < newStart) nextEnd = '';
    }
    setFilters({ start: newStart, end: nextEnd });
  };

  const handlePullHours = () => {
    if (!canPullHours) return;
    validatorQuery.refetch();
    setRefreshNowTs(Date.now());
  };

  // Nobody is shown a projection built on a guess about their day - not on
  // their own timesheet, and not when a coach opens it to review with them. A
  // guessed eight hours looked like the coach's own day and put the projection
  // off. Anyone looking at somebody else's timesheet here may already set their
  // working day (canSelectOthers, the same permission the server checks), so
  // the question is asked of whoever is on the page.
  const needsWorkdayAnswer = hasProjection && averageWorkdayHours == null;

  const showProjection = hasProjection && !needsWorkdayAnswer && !hideProjectionSetting;

  // Your own settings, or those of anyone whose record you may already manage -
  // the same permission that let you pick them in the first place, and the same
  // one the server checks on the write. A manager maintaining somebody's
  // working day is no different from maintaining their target hours.
  const canEditProjection = hasProjection && (isSelf || canSelectOthers);

  const updateEmployee = useUpdateEmployee();

  // The reviewer's manual checklist. Null when there is none to show - a range
  // of several pay periods, which a tick cannot belong to. Only a reviewer of
  // somebody else's timesheet may tick; everyone else sees where it stands.
  const reviewChecks = data?.reviewChecks || null;
  const updateReviewCheck = useUpdateTimesheetReviewCheck();
  const [savingCheckId, setSavingCheckId] = useState(null);
  // The check waiting on a confirmation to be unticked. Nothing is lost - the
  // tick stays on record, stamped with who undid it - but undoing somebody's
  // review is worth a second look, so it is asked rather than done.
  const [pendingUncheck, setPendingUncheck] = useState(null);

  const handleReviewCheck = async (checkId, checked) => {
    setSavingCheckId(checkId);
    try {
      await updateReviewCheck.mutateAsync({
        employeeId: selectedEmployeeId, start, end, checkId, checked,
      });
    } catch (err) {
      showToast('error', err?.message || 'Could not save the check.');
    } finally {
      setSavingCheckId(null);
    }
  };

  const handleSaveProjectionSettings = async ({ averageWorkdayHours: hours, hideProjection }) => {
    try {
      await updateEmployee.mutateAsync({
        // The employee on screen, not the one signed in. These are two different
        // people whenever a manager is looking at somebody else's timesheet.
        id: selectedEmployeeId,
        average_workday_hours: hours,
        // Only carried when the modal actually offered the toggle, which it does
        // not on somebody else's behalf: whether to show the projection is a
        // personal display preference, not a record a manager keeps.
        ...(hideProjection === undefined ? {} : { hide_hours_projection: hideProjection }),
      });
      setProjectionSettingsOpen(false);
      // The projection reads this employee's hours out of the timesheet payload,
      // which the employee-record invalidation does not cover - without this the
      // panel keeps showing the old day length until something else refetches.
      if (!isSelf) await validatorQuery.refetch();
      showToast('success', 'Projection settings saved.');
    } catch (err) {
      showToast('error', err?.message || 'Could not save projection settings.');
    }
  };

  // The gear lives on the projection's own header, so hiding the projection
  // would take the way back with it. This is that way back: a single line where
  // the panel was, rather than a preference stranded on another page.
  const projectionHiddenNotice = hasProjection && isSelf && hideProjectionSetting && !needsWorkdayAnswer;

  const selectedEmployeeName = isSelf
    ? `${auth.employee?.first_name || ''} ${auth.employee?.last_name || ''}`.trim()
    : (data?.employee?.name || employeeOptions.find((opt) => opt.value === selectedEmployeeId)?.label || '');

  const renderPeriodTag = (value) => (
    value === currentPeriodValue ? <Tag color="blue" size="sm">Current</Tag> : null
  );

  return (
    <div className="bdc-page view-hours-page view-hours-page--wide">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'View Hours' }]}
        icon="bi-clock-history"
        title="View Hours"
        actions={(
          <button type="button" className="bdc-btn" onClick={() => setHelpOpen(true)}>
            <i className="bi bi-question-circle" aria-hidden="true" />
            Help
          </button>
        )}
      />

      <ViewHoursHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        canSelectOthers={canSelectOthers}
        nonExempt={isPartTime}
      />

      <ProjectionSettingsModal
        open={projectionSettingsOpen}
        onClose={() => setProjectionSettingsOpen(false)}
        onSave={handleSaveProjectionSettings}
        saving={updateEmployee.isPending}
        firstRun={needsWorkdayAnswer}
        averageWorkdayHours={averageWorkdayHours}
        hideProjection={hideProjectionSetting}
        nonExempt={isPartTime}
        suggestedWorkdayHours={suggestedWorkdayHours}
        recentWeeklyAverage={recentWeeklyAverage}
        subjectName={isSelf ? null : (selectedEmployeeName || 'this employee')}
      />

      <FilterToolbar
        action={{ label: 'Refresh hours', onClick: handlePullHours, loading: isPullingHours, disabled: !canPullHours }}
        status={refreshedAgo ? `Synced from TimeCamp ${refreshedAgo} ago` : null}
      >
        {canSelectOthers ? (
          <FilterField label="Employee" basis={260} max={340}>
            <SelectPicker
              data={employeeOptions}
              value={selectedEmployeeId}
              onChange={(value) => setFilters({ employeeId: value || '' })}
              placeholder={employeesQuery.isLoading ? 'Loading…' : 'Select an employee'}
              block
              loading={employeesQuery.isLoading}
            />
          </FilterField>
        ) : selectedEmployeeName ? (
          <FilterReadonly label="Employee" basis={260}>{selectedEmployeeName}</FilterReadonly>
        ) : null}

        <FilterField label="Pay Period" basis={240} max={320}>
          <SelectPicker
            data={payPeriodOptions}
            value={startPeriod}
            onChange={handleStartPeriodChange}
            cleanable={false}
            searchable={false}
            block
            renderMenuItem={(label, item) => (
              <span className="d-flex align-items-center gap-2">
                <span>{label}</span>
                {renderPeriodTag(item.value)}
              </span>
            )}
            renderValue={(value, item) => (
              <span className="d-flex align-items-center gap-2">
                <span>{item?.label}</span>
                {renderPeriodTag(value)}
              </span>
            )}
          />
        </FilterField>

        <FilterField label="Through" basis={240} max={320}>
          <SelectPicker
            data={endPeriodOptions}
            value={endPeriod}
            onChange={(value) => setFilters({ end: value ? String(value).split('|')[1] : '' })}
            placeholder="Single period"
            searchable={false}
            block
            renderMenuItem={(label, item) => (
              <span className="d-flex align-items-center gap-2">
                <span>{label}</span>
                {renderPeriodTag(item.value)}
              </span>
            )}
          />
        </FilterField>

        {/* Shown, not chosen: it belongs to the employee, and changing it here
            would only produce a number that matches nobody's actual hours.
            Non-exempt employees are not held to a TAWH commitment, so there
            is nothing to show them here at all. */}
        {isPartTime ? null : (
          <FilterReadonly label={<HoursMeasureLabel />} basis={160}>
            {rangePercents.length > 1
              ? rangePercents.map((pct) => `${pct}%`).join(' → ')
              : `${selectedPercentNumber}%`}
          </FilterReadonly>
        )}
      </FilterToolbar>

      {!isRangeMode || isPartTime ? (
        <Panel bordered className="view-hours-result-card">
          <div className={`view-hours-split${showProjection || needsWorkdayAnswer ? ' view-hours-split--paired' : ''}`}>
            <section className="view-hours-split-side">
              {showProjection || needsWorkdayAnswer ? (
                <div className="view-hours-side-eyebrow">Actual</div>
              ) : null}
              <div className="text-muted small">
                {periodTarget == null
                  ? `Hours worked ${isRangeMode ? 'this range' : 'this pay period'}`
                  : `Hours to work ${isRangeMode ? 'this range' : 'this pay period'}`}
              </div>
              <div className="view-hours-big-number">
                {/* Without a target there is nothing to subtract from, so the
                    hours worked stand on their own. */}
                {periodTarget == null ? (
                  workedHours === undefined ? 'Not available' : `${formatHours(workedHours)} hrs`
                ) : (
                  workedHours !== undefined && remainingHours !== undefined ? (
                    <span
                      className="view-hours-inline-math"
                      style={{ gridTemplateColumns: 'repeat(5, auto)' }}
                      aria-label="Hours to work minus hours worked equals hours left to work"
                    >
                      <EquationTerm
                        column={1}
                        tone="work"
                        value={formatHours(periodTarget)}
                        label={isPartTime ? 'Target hours' : 'Hours to work'}
                      />
                      <span className="view-hours-inline-op" style={{ gridColumn: 2, gridRow: 1 }}>-</span>
                      <EquationTerm
                        column={3}
                        tone="worked"
                        value={formatHours(workedHours)}
                        label="Hours worked"
                      />
                      <span className="view-hours-inline-op view-hours-inline-op--equals" style={{ gridColumn: 4, gridRow: 1 }}>=</span>
                      <EquationTerm
                        column={5}
                        tone="remaining"
                        value={formatHours(remainingHours)}
                        unit
                        label="Left to work"
                      />
                    </span>
                  ) : (
                    `${formatHours(periodTarget)} hrs`
                  )
                )}
              </div>
            </section>

            {showProjection ? (
              <section className="view-hours-split-side">
                <div className="view-hours-side-eyebrow view-hours-side-eyebrow--withaction">
                  Projected
                  {canEditProjection ? (
                    <Whisper
                      trigger="hover"
                      placement="top"
                      speaker={<Tooltip>Projection settings</Tooltip>}
                    >
                      <button
                        type="button"
                        className="view-hours-eyebrow-gear"
                        onClick={() => setProjectionSettingsOpen(true)}
                        aria-label="Projection settings"
                      >
                        <i className="bi bi-gear" aria-hidden="true" />
                      </button>
                    </Whisper>
                  ) : null}
                </div>
                <div className="text-muted small">
                  Over or under target, at {formatHours(projectionDayHours)} hrs a day
                </div>
                <div className="view-hours-big-number">
                  <span
                    className="view-hours-inline-math"
                    style={{ gridTemplateColumns: `repeat(${projectionLayout.columns}, auto)` }}
                    aria-label={projectionAriaLabel}
                  >
                    {projectionLayout.terms.map((term) => (
                      <React.Fragment key={term.key}>
                        {term.op ? (
                          <span className="view-hours-inline-op" style={{ gridColumn: term.opColumn, gridRow: 1 }}>
                            {term.op}
                          </span>
                        ) : null}
                        <EquationTerm
                          column={term.termColumn}
                          tone={term.tone}
                          value={formatHours(term.value)}
                          unit={term.unit}
                          label={term.label}
                          tooltip={term.tooltip}
                          warning={term.warning}
                        />
                      </React.Fragment>
                    ))}

                    <span className="view-hours-inline-op view-hours-inline-op--equals" style={{ gridColumn: projectionLayout.equalsColumn, gridRow: 1 }}>=</span>
                    <EquationTerm
                      column={projectionLayout.resultColumn}
                      tone={isOnTrack ? 'remaining' : 'alert'}
                      value={`${projectedVariance > 0 ? '+' : ''}${formatHours(projectedVariance)}`}
                      unit
                      label={isOnTrack ? 'on track' : (projectedVariance > 0 ? 'over target' : 'under target')}
                    />
                  </span>
                </div>
                <div className="view-hours-projection-basis">
                  {projectionBasis || 'No standard hours left in this period.'}
                </div>
              </section>
            ) : null}

            {/* The question, in the place the answer will go. Put here rather
                than behind a modal on load: an unprompted dialog over a page
                somebody opened to read a number gets dismissed, and then the
                projection is missing with nothing on screen saying why. */}
            {needsWorkdayAnswer ? (
              <section className="view-hours-split-side">
                <div className="view-hours-side-eyebrow">Projected</div>
                <div className="view-hours-projection-ask">
                  <div className="view-hours-projection-ask__text">
                    {isSelf ? (
                      <>
                        This section is intended to help you plan your time off and
                        committed hours. To get started, enter some info about your
                        standard working day.
                      </>
                    ) : (
                      <>
                        {selectedEmployeeName || 'This employee'} hasn&apos;t set their
                        standard working day yet, so there is nothing to project from.
                        Ask them how long their day usually is and enter it here.
                      </>
                    )}
                  </div>
                  <Button
                    appearance="primary"
                    size="sm"
                    onClick={() => setProjectionSettingsOpen(true)}
                  >
                    {isSelf ? 'Set my working day' : 'Set their working day'}
                  </Button>
                </div>
              </section>
            ) : null}
          </div>

          {projectionHiddenNotice ? (
            <div className="view-hours-projection-hidden">
              Projection hidden.
              <button
                type="button"
                className="view-hours-projection-hidden__action"
                onClick={() => setProjectionSettingsOpen(true)}
              >
                <i className="bi bi-gear" aria-hidden="true" /> Settings
              </button>
            </div>
          ) : null}
        </Panel>
      ) : (
        <Panel bordered>
          <div className="text-muted small mb-2">Hours to work per pay period</div>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Pay Period</th>
                  <th className="text-end">Commitment</th>
                  <th className="text-end">Hours to work</th>
                </tr>
              </thead>
              <tbody>
                {rangeRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-muted">This range does not cover a whole pay period.</td>
                  </tr>
                ) : rangeRows.map((row) => {
                  // A period inside an exempt range can itself have been
                  // non-exempt, when the range spans a change of classification.
                  return (
                    <tr key={row.id}>
                      <td>{row.title}</td>
                      <td className="text-end">{row.nonExempt ? 'Non-exempt' : `${row.tawhPercent}%`}</td>
                      <td className="text-end">
                        {row.hoursToWork == null ? 'No target' : `${formatHours(row.hoursToWork)} hrs`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {canSelectOthers && employeesQuery.isError ? (
        <Message type="error" showIcon>
          Could not load employees: {String(employeesQuery.error?.message || employeesQuery.error)}
        </Message>
      ) : null}

      {planReminders.length > 0 ? (
        <div className="viewhours-plan-reminders" role="note">
          <div className="viewhours-plan-reminders__head">
            <i className="bi bi-airplane" aria-hidden="true" />
            <span>
              {new Set(planReminders.map((reminder) => reminder.planId)).size > 1
                ? 'You have submitted vacation plans'
                : 'You have a submitted vacation plan'}
              {isRangeMode ? ' for these pay periods' : ' for this pay period'}
              {new Set(planReminders.map((reminder) => reminder.planId)).size > 1
                ? ' that have the following assumptions'
                : ' that has the following assumptions'}
            </span>
          </div>

          {/* Extra hours beside unpaid time, a column each - one column when
              only one of them applies. */}
          <div className="viewhours-plan-reminders__columns">
            {[
              {
                key: 'extra',
                title: 'Extra hours to work',
                icon: 'bi-clock-history',
                items: planReminders.filter((reminder) => reminder.extraHours > 0),
                hours: (reminder) => reminder.extraHours,
                where: (reminder) => (reminder.periodLabel ? `during ${reminder.periodLabel}` : ''),
              },
              {
                key: 'unpaid',
                title: 'Time off taken unpaid',
                icon: 'bi-cash-coin',
                items: planReminders.filter((reminder) => reminder.unpaidHours > 0),
                hours: (reminder) => reminder.unpaidHours,
                where: (reminder) => (reminder.periodLabel ? `in ${reminder.periodLabel}` : 'across the trip'),
              },
            ].filter((column) => column.items.length > 0).map((column) => (
              <section key={column.key} className={`viewhours-plan-reminders__col viewhours-plan-reminders__col--${column.key}`}>
                <div className="viewhours-plan-reminders__coltitle">
                  <i className={`bi ${column.icon}`} aria-hidden="true" />
                  {column.title}
                </div>
                <ul className="viewhours-plan-reminders__list">
                  {column.items.map((reminder) => {
                    const dates = formatTripRange(reminder.planStart, reminder.planEnd);
                    return (
                      <li key={`${column.key}-${reminder.planId}-${reminder.periodId || 'trip'}`}>
                        <div className="viewhours-plan-reminders__item">
                          <strong>{hrs(column.hours(reminder))}</strong> {column.where(reminder)}
                        </div>
                        <div className="viewhours-plan-reminders__plan">
                          for <Link to={`/time-off/planner/${reminder.planId}`}>{reminder.planName}</Link>
                          {/* A plan named for its dates already says them. */}
                          {reminder.planName === dates ? '' : ` (${dates})`}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        </div>
      ) : null}

      {!selectedEmployeeId ? (
        <Message type="info" showIcon>
          Select an employee to view their timesheet summary.
        </Message>
      ) : validatorQuery.isError ? (
        <Message type="error" showIcon>
          Could not load timesheet: {String(validatorQuery.error?.message || validatorQuery.error)}
        </Message>
      ) : validatorQuery.isLoading ? (
        <Panel bordered>
          <Placeholder.Grid rows={4} columns={3} active />
        </Panel>
      ) : summary ? (
        <>
          <Section
            title="Summary"
            note={[data.employee?.name, periodLabel].filter(Boolean).join(' · ') || null}
          >
            <section className="bdc-metrics">
              <SummaryStat
                label="Total Hours"
                value={metricHours(summary.totalHours)}
                foot={isPartTime && recentWeeklyAverage
                  ? `${formatHours(recentWeeklyAverage.hoursPerWeek)} hrs/week over the last ${recentWeeklyAverage.weeks} weeks`
                  : undefined}
              />
              <SummaryStat
                label="Billable %"
                value={Number.isFinite(Number(summary.billablePercentage))
                  ? `${formatHours(summary.billablePercentage)}%`
                  : '—'}
                foot={`${formatHours(summary.billableHours)} of ${formatHours(summary.totalHours)} hrs billable`}
              />
              <TimeOffStat timeOff={data.timeOff} />
            </section>
          </Section>

          <div className="d-flex flex-wrap gap-3 align-items-stretch">
            <div className="nsi-table-wrap" style={{ flex: '1 1 360px', minWidth: 320 }}>
              <div className="bdc-table-head">
                <h3 className="bdc-table-head__title">Validations</h3>
              </div>
              <table className="nsi-table bdc-table--narrow">
                <thead>
                  <tr>
                    <th>Check</th>
                    <th className="nsi-table__right">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.validations || []).length === 0 ? (
                    <tr>
                      <td colSpan={2} className="nsi-table__empty">No validations to run.</td>
                    </tr>
                  ) : data.validations.map((validation) => {
                    // Only a check with days behind it has anything to open,
                    // which is either of the two ways of not passing.
                    const severity = validationSeverity(validation);
                    const flagged = severity !== 'pass';
                    const failed = severity === 'fail';
                    const open = () => setOpenValidation(validation);
                    const row = (
                      <tr
                        key={validation.id}
                        className={flagged ? 'nsi-table__row' : 'view-hours-validation-row'}
                        onClick={flagged ? open : undefined}
                        onKeyDown={flagged ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            open();
                          }
                        } : undefined}
                        tabIndex={flagged ? 0 : undefined}
                        role={flagged ? 'button' : undefined}
                      >
                        <td className="bdc-table-wrap">{validation.name}</td>
                        <td className="nsi-table__right">
                          {flagged ? (
                            <span
                              className={`view-hours-validation-result ${failed ? 'bdc-text-bad' : 'bdc-text-warn'}`}
                            >
                              <i
                                className={failed ? 'bi bi-x-circle-fill' : 'bi bi-exclamation-triangle-fill'}
                                aria-hidden="true"
                              />
                              <span>{validationResultLabel(validation)} ›</span>
                            </span>
                          ) : (
                            <span className="view-hours-validation-result bdc-text-good">
                              <i
                                className="bi bi-check-circle-fill"
                                role="img"
                                aria-label="Passed"
                              />
                            </span>
                          )}
                        </td>
                      </tr>
                    );

                    // The row names the check in a few words; what it actually
                    // takes to pass is the thing somebody looking at either
                    // mark wants next. It is the same sentence the failure
                    // dialog opens with, straight off the rule itself, so the
                    // two cannot drift apart.
                    //
                    // The native title attributes this replaces are gone: a
                    // browser tooltip would open on top of this one.
                    if (!validation.description) return row;

                    return (
                      <Whisper
                        key={validation.id}
                        trigger="hover"
                        placement="top"
                        delayOpen={200}
                        speaker={(
                          <Tooltip className="view-hours-validation__tip">
                            {validation.description}
                            {flagged ? (
                              <div className="view-hours-validation__tip-hint">
                                Click to see the {validation.unit === 'entry' ? 'entries' : 'days'}
                                {' '}{failed ? 'that failed' : 'to look at'}.
                              </div>
                            ) : null}
                          </Tooltip>
                        )}
                      >
                        {row}
                      </Whisper>
                    );
                  })}
                </tbody>
              </table>

              {/* The judgement calls no rule can make, under the rules that can.
                  Kept to their own table rather than mixed into the rows above:
                  those report what the timesheet says, these record what a
                  person decided about it. */}
              {reviewChecks && reviewChecks.checks.length > 0 ? (
                <table className="nsi-table bdc-table--narrow view-hours-review-checks">
                  <thead>
                    <tr>
                      <th>Reviewer check</th>
                      <th className="nsi-table__right">Done</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewChecks.checks.map((check) => (
                      <tr key={check.id}>
                        <td className="bdc-table-wrap">
                          {check.question}
                          {check.checked ? (
                            <div className="view-hours-review-checks__by">
                              Checked by {check.checkedBy?.name || 'a former employee'}
                              {check.checkedAt ? ` · ${formatCheckedAt(check.checkedAt)}` : ''}
                            </div>
                          ) : check.uncheckedAt ? (
                            // Taken back, not wiped: the box is empty, but the
                            // record of who checked it and who undid it stays.
                            <div className="view-hours-review-checks__by">
                              Unchecked by {check.uncheckedBy?.name || 'a former employee'}
                              {` · ${formatCheckedAt(check.uncheckedAt)}`}
                              {check.checkedBy
                                ? ` (checked by ${check.checkedBy.name || 'a former employee'}`
                                  + `${check.checkedAt ? ` · ${formatCheckedAt(check.checkedAt)}` : ''})`
                                : ''}
                            </div>
                          ) : null}
                        </td>
                        <td className="nsi-table__right">
                          {/* Always the box, so the column reads the same to
                              everyone; it is only live for a reviewer, and
                              says why when it is not. */}
                          {reviewChecks.canReview ? (
                            <Checkbox
                              className="view-hours-review-checks__box"
                              checked={check.checked}
                              disabled={savingCheckId === check.id}
                              onChange={(_value, checked) => (checked
                                ? handleReviewCheck(check.id, true)
                                : setPendingUncheck(check))}
                              aria-label={check.question}
                            />
                          ) : (
                            <Whisper
                              trigger="hover"
                              placement="top"
                              speaker={<Tooltip>Only a reviewer of this timesheet can check this.</Tooltip>}
                            >
                              <span>
                                <Checkbox
                                  className="view-hours-review-checks__box"
                                  checked={check.checked}
                                  disabled
                                  aria-label={check.question}
                                />
                              </span>
                            </Whisper>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>

            <div className="nsi-table-wrap" style={{ flex: '1 1 360px', minWidth: 320 }}>
              <div className="bdc-table-head">
                <h3 className="bdc-table-head__title">Top Tasks</h3>
              </div>
              <table className="nsi-table bdc-table--narrow">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th>Project / Task</th>
                    <th className="nsi-table__right">Hours</th>
                    <th className="nsi-table__right">Entries</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.topTasks.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="nsi-table__empty">No tasks logged for this period.</td>
                    </tr>
                  ) : summary.topTasks.map((task, idx) => (
                    <tr key={task.taskId}>
                      <td className="bdc-num">{idx + 1}</td>
                      <td className="bdc-table-wrap">{taskLabel(task)}</td>
                      <td className="nsi-table__right bdc-num">{formatHours(task.totalHours)}</td>
                      <td className="nsi-table__right bdc-num">{task.entryCount ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Keyed on what it lists: a different employee or period is a
              different list, so it opens collapsed and back on newest first. */}
          <EntriesTable key={`${selectedEmployeeId}|${start}|${end}`} entries={allEntries} />
        </>
      ) : null}

      <ConfirmModal
        open={Boolean(pendingUncheck)}
        title="Uncheck reviewer check?"
        body={(
          <>
            <p>{pendingUncheck?.question}</p>
            <p className="text-muted mb-0">
              {pendingUncheck?.checkedBy
                ? `Checked by ${pendingUncheck.checkedBy.name || 'a former employee'}`
                  + `${pendingUncheck.checkedAt ? ` on ${formatCheckedAt(pendingUncheck.checkedAt)}` : ''}. `
                : ''}
              That stays on record, and the check will show as unchecked by you.
            </p>
          </>
        )}
        confirmText="Uncheck"
        danger
        onConfirm={() => {
          const check = pendingUncheck;
          setPendingUncheck(null);
          handleReviewCheck(check.id, false);
        }}
        onCancel={() => setPendingUncheck(null)}
      />

      <Modal open={Boolean(openValidation)} onClose={() => setOpenValidation(null)} size="lg">
        <Modal.Header>
          <Modal.Title>{validationShown?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationShown?.description ? (
            <p className="text-muted">{validationShown.description}</p>
          ) : null}
          {(validationShown?.failures || []).length === 0 ? (
            <div className="text-muted">No failures.</div>
          ) : (
            validationShown.failures.map((failure, idx) => (
              <div key={`${failure.date ?? ''}-${idx}`} className="nsi-table-wrap mb-4">
                <div className="bdc-table-head">
                  <div className="bdc-table-head__heading">
                    <h3 className="bdc-table-head__title">{failure.date}</h3>
                    <span className="bdc-table-head__note">
                      {formatHours(failure.totalHours)} hrs logged
                    </span>
                  </div>
                  <span
                    className={`bdc-table-head__note ${failureSeverity(failure) === 'warn' ? 'bdc-text-warn' : 'bdc-text-bad'}`}
                  >
                    {failure.detail}
                  </span>
                </div>
                <table className="nsi-table bdc-table--narrow">
                  <thead>
                    <tr>
                      <th>Project / Task</th>
                      <th>Description</th>
                      <th className="nsi-table__right">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(failure.entries || []).length === 0 ? (
                      <tr>
                        <td colSpan={3} className="nsi-table__empty">No entries logged that day.</td>
                      </tr>
                    ) : failure.entries.map((entry, entryIdx) => (
                      <tr key={entry.id || `${failure.date}-${entryIdx}`}>
                        <td className="bdc-table-wrap">{taskLabel(entry)}</td>
                        <td className="bdc-table-wrap nsi-table__muted">
                          {entry.description || '—'}
                        </td>
                        <td className="nsi-table__right bdc-num">{formatHours(entry.durationHours)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setOpenValidation(null)}>Close</NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
